import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export function initTaiikuRhythm(): void {
    const circles = document.querySelectorAll('.circle-rhythm');
    if (circles.length === 0) return;

    gsap.to(circles, {
        scale: (i) => 1.2 + i * 0.2,
        opacity: (i) => 0.1 - i * 0.02,
        duration: 2,
        repeat: -1,
        yoyo: true,
        stagger: {
            each: 0.5,
            repeat: -1,
            yoyo: true
        },
        ease: 'sine.inOut'
    });
}

export function initNakamaNetwork(): void {
    const core = document.querySelector('.network-core');
    if (!core) return;

    gsap.to(core, {
        boxShadow: '0 0 50px rgba(237, 41, 57, 0.6)',
        duration: 2,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut'
    });
}

export function initProductGalleries(): void {
    document.querySelectorAll<HTMLElement>('[data-prod-gallery]').forEach(gallery => {
        const imgs = Array.from(gallery.querySelectorAll<HTMLElement>('.prod-gallery__img'));
        const thumbs = Array.from(gallery.querySelectorAll<HTMLButtonElement>('.prod-gallery__thumb'));
        if (!imgs.length || !thumbs.length) return;

        const setActive = (index: number, moveFocus = false): void => {
            imgs.forEach((img, i) => img.classList.toggle('is-active', i === index));
            thumbs.forEach((thumb, i) => {
                thumb.classList.toggle('is-active', i === index);
                thumb.setAttribute('aria-selected', (i === index).toString());
                thumb.tabIndex = i === index ? 0 : -1;
            });
            if (moveFocus) thumbs[index]?.focus({ preventScroll: true });
        };

        const initialIndex = Math.max(0, thumbs.findIndex(thumb => thumb.classList.contains('is-active')));
        setActive(initialIndex);

        thumbs.forEach((thumb, i) => {
            thumb.addEventListener('click', () => setActive(i));
            thumb.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActive(i); }
                if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                    e.preventDefault();
                    setActive((i + 1) % thumbs.length, true);
                }
                if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                    e.preventDefault();
                    setActive((i - 1 + thumbs.length) % thumbs.length, true);
                }
            });
        });
    });
}

export function initKaizenDoScreens(): void {
    const lightbox = document.querySelector<HTMLElement>('[data-kd-lightbox]');
    const triggers = Array.from(document.querySelectorAll<HTMLButtonElement>('[data-kd-screen]'));
    if (!lightbox || triggers.length === 0) return;

    const image = lightbox.querySelector<HTMLImageElement>('[data-kd-lightbox-img]');
    const title = lightbox.querySelector<HTMLElement>('[data-kd-lightbox-title]');
    const closeButtons = Array.from(lightbox.querySelectorAll<HTMLElement>('[data-kd-lightbox-close]'));
    const closeButton = lightbox.querySelector<HTMLButtonElement>('.kd-lightbox__close');
    let activeTrigger: HTMLButtonElement | null = null;
    let previousBodyOverflow = '';

    const close = (): void => {
        if (!lightbox.classList.contains('is-open')) return;

        lightbox.classList.remove('is-open');
        lightbox.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = previousBodyOverflow;

        if (image) {
            image.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==';
            image.alt = '';
        }

        activeTrigger?.focus({ preventScroll: true });
        activeTrigger = null;
    };

    const open = (trigger: HTMLButtonElement): void => {
        const src = trigger.dataset.src;
        if (!src || !image || !title) return;

        activeTrigger = trigger;
        previousBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        image.src = new URL(src, window.location.origin).toString();
        image.alt = trigger.dataset.caption ?? '';
        title.textContent = trigger.dataset.title ?? '';

        lightbox.classList.add('is-open');
        lightbox.setAttribute('aria-hidden', 'false');
        closeButton?.focus({ preventScroll: true });
    };

    triggers.forEach(trigger => {
        trigger.addEventListener('click', () => open(trigger));
    });

    closeButtons.forEach(button => {
        button.addEventListener('click', close);
    });

    document.addEventListener('keydown', event => {
        if (event.key === 'Escape') {
            close();
        }
    });
}

export function initBankaGold(): void {
    const lines = document.querySelectorAll('.gold-line');
    if (lines.length === 0) return;

    gsap.from(lines, {
        width: 0,
        stagger: 0.5,
        duration: 2,
        ease: 'power4.inOut',
        scrollTrigger: {
            trigger: '.gold-flow-visual',
            start: 'top 80%'
        }
    });
}
